import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Tippy, { useSingleton } from '@tippyjs/react'
import type { Instance as TippyInstance } from 'tippy.js'
import { useTranslation } from 'react-i18next'
import { ConsentContext } from '@context/ConsentContext'
import { PredictionContext } from '@context/PredictionContext'
import { SurveyContext } from '@context/SurveyContext'
import ModalDialog from '@components/ModalDialog/ModalDialog'
import { DsmpChart } from '@components/Survey/Dsmp/DsmpChart'
import { PredictionBars } from '@components/Survey/TabbedPane/PredictionBars'
import { isPredictionResults, ResultsChartView } from '@components/Survey/TabbedPane/ResultsChartView'
import { isIntegratedApp } from '@models/AppType'
import { PredictionResults } from '@models/Prediction/Prediction'
import { SurveyStep } from '@models/Survey/SurveyStep'
import {
  categorySurveyQuestionCount,
  getCategory,
  isDsmpPrediction,
  sortedCategorySurveys,
} from '@utils/categoryFlow'
import {
  buildEditionProgressSegments,
  ProgressSegment,
} from '@utils/editionProgress'
import gotchiIcon from '@assets/images/characters/gotchi.png'
import 'tippy.js/dist/tippy.css'
import './EditionProgressBar.scss'

type ModalState =
  | { type: 'portrait'; src: string; title: string }
  | { type: 'chart'; segment: ProgressSegment; title: string }
  | null

/** Sticky mini chart for progress tooltip (PredictionBars / compact DSMP). */
function ResultsChartThumb({
  segment,
  snapshot,
  dsmpQ1,
  dsmpQ2,
}: {
  segment: ProgressSegment
  snapshot: PredictionResults
  dsmpQ1?: number
  dsmpQ2?: number
}) {
  if (isDsmpPrediction(snapshot.prediction)) {
    const score = snapshot.prediction.dsmpScore ?? snapshot.prediction.dsmp_score
    return (
      <div className="EditionProgressBar-tooltip-dsmpThumb">
        <DsmpChart
          dsmpQ1={segment.resultVariant === 'intermediate' ? score ?? dsmpQ1 : dsmpQ1 ?? score}
          dsmpQ2={segment.resultVariant === 'final' ? dsmpQ2 ?? score : undefined}
        />
      </div>
    )
  }
  return (
    <div className="EditionProgressBar-tooltip-predictionBars">
      <PredictionBars predictionResults={snapshot} interactive={false} />
    </div>
  )
}

function segmentLabel(
  segment: ProgressSegment,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (segment.kind === 'questionnaire') {
    return t('questionnaire', {
      index: (segment.surveyIndex ?? 0) + 1,
      count: segment.surveyCount ?? 1,
    })
  }
  if (segment.pageId === 'results') {
    return segment.resultVariant === 'intermediate' ? t('resultsIntermediate') : t('resultsFinal')
  }
  return t(segment.pageId ?? 'consent')
}

function statusLabel(
  status: ProgressSegment['status'],
  t: (key: string) => string,
): string {
  if (status === 'completed') return t('status.completed')
  if (status === 'current') return t('status.current')
  return t('status.upcoming')
}

/** Steps where ResultWidget shows BarChart (portrait hidden). */
const CHART_TOP_STEPS = new Set<SurveyStep>([
  SurveyStep.Prediction,
  SurveyStep.Rectify,
  SurveyStep.Probability,
  SurveyStep.Forecasting,
  SurveyStep.Results,
  SurveyStep.ResultsIntermediate,
])

function isPortraitVisibleAtTop(step: SurveyStep): boolean {
  return !CHART_TOP_STEPS.has(step)
}

function isChartVisibleAtTop(step: SurveyStep): boolean {
  return CHART_TOP_STEPS.has(step)
}

function segmentTypeClass(segment: ProgressSegment): string {
  if (segment.kind === 'questionnaire') {
    return 'EditionProgressBar-segment--type-questionnaire'
  }
  return `EditionProgressBar-segment--type-${segment.pageId ?? 'consent'}`
}

function predictionsMatch(a: unknown, b: unknown): boolean {
  if (!a || !b || a === b) {
    return a === b
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

const TOP_MEDIA_OBSCURE_PX = 10

/** True when the top portrait/chart is scrolled under the sticky nav (or off-screen) by >10px. */
function measureTopMediaObscured(): boolean {
  const portrait = document.querySelector('.PortraitWidget:not(.hidden)') as HTMLElement | null
  const chartRoot = document.querySelector('.ResultWidget .BarChartWidget') as HTMLElement | null
  const target =
    chartRoot && chartRoot.offsetParent !== null
      ? chartRoot
      : portrait && portrait.offsetParent !== null
        ? portrait
        : (document.querySelector('.ResultWidget') as HTMLElement | null)
  if (!target) {
    return true
  }
  const rect = target.getBoundingClientRect()
  const sticky = document.querySelector('.TabbedPane-body-navigation.is-sticky') as HTMLElement | null
  const clipTop = sticky ? sticky.getBoundingClientRect().bottom : 0
  const hiddenTop = Math.max(0, clipTop - rect.top)
  return hiddenTop > TOP_MEDIA_OBSCURE_PX || rect.bottom <= clipTop + TOP_MEDIA_OBSCURE_PX
}

const TIPPY_EDGE_PAD = 16
const TIPPY_BELOW_GAP = 6

/** Central app column — tippies must stay inside this box. */
function tippyBoundary(): HTMLElement {
  return (document.querySelector('.AppLayout') as HTMLElement | null) ?? document.body
}

function tippyMaxWidth(): number {
  const app = tippyBoundary()
  const width = app.getBoundingClientRect?.().width ?? app.clientWidth ?? 320
  return Math.max(160, Math.min(280, Math.floor(width - TIPPY_EDGE_PAD * 2)))
}

type PopperMainState = {
  modifiersData: { popperOffsets?: { x: number; y: number } }
  rects: { popper: { width: number } }
  elements: { popper: HTMLElement }
}

/** After preventOverflow: force X into the app column with a comfortable inset. */
function clampTippyToAppColumnModifier() {
  return {
    name: 'clampToAppColumn',
    enabled: true,
    phase: 'main' as const,
    requiresIfExists: ['preventOverflow'],
    fn({ state }: { state: PopperMainState }) {
      const offsets = state.modifiersData.popperOffsets
      if (!offsets) return
      const offsetParent = state.elements.popper.offsetParent
      const parentWidth =
        offsetParent instanceof HTMLElement
          ? offsetParent.clientWidth
          : tippyBoundary().clientWidth
      const w = state.rects.popper.width
      const minX = TIPPY_EDGE_PAD
      const maxX = Math.max(TIPPY_EDGE_PAD, parentWidth - TIPPY_EDGE_PAD - w)
      offsets.x = Math.min(Math.max(offsets.x, minX), maxX)
    },
  }
}

/** Lock tooltip top edge just under the bar — ignore content height / overflow shifts. */
function pinTippyYBelowBarModifier(getBar: () => HTMLElement | null) {
  return {
    name: 'pinTippyYBelowBar',
    enabled: true,
    phase: 'main' as const,
    fn({ state }: { state: PopperMainState }) {
      const offsets = state.modifiersData.popperOffsets
      const bar = getBar()
      if (!offsets || !bar) return
      const barBottom = bar.getBoundingClientRect().bottom
      const offsetParent = state.elements.popper.offsetParent
      if (offsetParent instanceof HTMLElement) {
        offsets.y = barBottom - offsetParent.getBoundingClientRect().top + TIPPY_BELOW_GAP
      } else {
        offsets.y = barBottom + TIPPY_BELOW_GAP
      }
    },
  }
}

/** Anchor X to the segment slot, but always use the bar’s fixed top/bottom for Y. */
function slotReferenceRect(slot: HTMLElement, bar: HTMLElement): DOMRect {
  const slotRect = slot.getBoundingClientRect()
  const barRect = bar.getBoundingClientRect()
  return {
    width: slotRect.width,
    height: barRect.height,
    top: barRect.top,
    bottom: barRect.bottom,
    left: slotRect.left,
    right: slotRect.right,
    x: slotRect.left,
    y: barRect.top,
    toJSON() {
      return this
    },
  } as DOMRect
}

export const EditionProgressBar = memo(function EditionProgressBar() {
  const { t, i18n } = useTranslation('EditionProgress')
  const { hasConsented, hasReadConsent, hasConditions, isMajor } = useContext(ConsentContext)
  const {
    survey,
    step,
    surveyIsCompleted,
    currentPosition = 0,
    questionsCount,
    hasAcknowledged,
    email,
    portraitBySurveyKey = {},
    hasVisitedRectify = false,
    resultVariant,
    resultsSnapshotsBySurveyKey = {},
    questionnaireStatsBySurveyKey = {},
    questionsList,
  } = useContext(SurveyContext)
  const {
    results,
    prediction,
    predictionVeracity,
    rectifiedPrediction,
    voteProbability,
    citizenForecasting,
    dsmpQ1,
    dsmpQ2,
  } = useContext(PredictionContext)

  const [modal, setModal] = useState<ModalState>(null)
  const [expandedSegmentId, setExpandedSegmentId] = useState<string | null>(null)
  const [topMediaObscured, setTopMediaObscured] = useState(false)
  const barRef = useRef<HTMLDivElement | null>(null)
  // Do not override placement — children default to `top`, which would win over the source.
  const [tippySource, tippyTarget] = useSingleton()
  const lang = (i18n.language ?? 'fr').substring(0, 2)

  useEffect(() => {
    const update = () => setTopMediaObscured(measureTopMediaObscured())
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [step])

  const questionnaireLengthsByKey = useMemo(() => {
    const lengths: Record<string, number> = {}
    // Prefer counts from survey payload (active_surveys.question_count).
    for (const ref of sortedCategorySurveys(getCategory(survey))) {
      const count = categorySurveyQuestionCount(ref)
      if (count != null) {
        lengths[ref.key] = count
      }
    }
    for (const [key, stats] of Object.entries(questionnaireStatsBySurveyKey)) {
      if (stats?.totalCount && stats.totalCount > 0) {
        lengths[key] = stats.totalCount
      }
    }
    if (survey?.key && questionsList.length > 0) {
      lengths[survey.key] = questionsList.length
    }
    return lengths
  }, [questionnaireStatsBySurveyKey, survey, questionsList.length])

  const segments = useMemo(() => {
    const built = buildEditionProgressSegments({
      category: getCategory(survey),
      currentSurvey: survey,
      step,
      hasConsented: !!hasConsented,
      surveyIsCompleted,
      currentPosition,
      questionsCount,
      isIntegrated: isIntegratedApp(),
      hasVisitedRectify,
      resultVariant,
      questionnaireLengthsByKey,
    })
    return built.map((seg) => {
      if (seg.kind !== 'questionnaire' || !seg.surveyKey) {
        return seg
      }
      const stats = questionnaireStatsBySurveyKey[seg.surveyKey]
      const isCurrentSurvey = seg.surveyKey === survey?.key
      // Live totals while this survey is loaded; frozen stats for completed / prior surveys.
      const liveTotal = isCurrentSurvey && questionsList.length > 0 ? questionsList.length : undefined
      const liveVisible = isCurrentSurvey && questionsCount > 0 ? questionsCount : undefined
      const totalQuestionCount =
        (liveTotal && liveTotal > 0 ? liveTotal : undefined) ??
        (stats?.totalCount && stats.totalCount > 0 ? stats.totalCount : undefined)
      const visibleQuestionCount =
        (liveVisible && liveVisible > 0 ? liveVisible : undefined) ??
        (stats?.visibleCount && stats.visibleCount > 0 ? stats.visibleCount : undefined) ??
        totalQuestionCount
      return {
        ...seg,
        visibleQuestionCount,
        totalQuestionCount,
      }
    })
  }, [
    survey,
    step,
    hasConsented,
    surveyIsCompleted,
    currentPosition,
    questionsCount,
    questionsList.length,
    hasVisitedRectify,
    resultVariant,
    questionnaireStatsBySurveyKey,
    questionnaireLengthsByKey,
  ])

  const resolveSnapshot = useCallback(
    (segment: ProgressSegment): PredictionResults | undefined => {
      if (segment.surveyKey && isPredictionResults(resultsSnapshotsBySurveyKey[segment.surveyKey])) {
        return resultsSnapshotsBySurveyKey[segment.surveyKey] as PredictionResults
      }
      if (segment.resultVariant === 'final' && results) {
        return results
      }
      if (segment.status !== 'upcoming' && results) {
        return results
      }
      return undefined
    },
    [results, resultsSnapshotsBySurveyKey],
  )

  /**
   * Hide thumb/chart only when the same media is still visibly on screen in ResultWidget.
   * If scrolled away by more than 10px, show thumb + open action again.
   */
  const isMediaAlreadyShownAtTop = useCallback(
    (segment: ProgressSegment): boolean => {
      if (topMediaObscured) {
        return false
      }
      if (segment.kind === 'questionnaire') {
        if (!isPortraitVisibleAtTop(step)) {
          return false
        }
        return !!segment.surveyKey && segment.surveyKey === survey?.key
      }
      if (segment.pageId === 'results') {
        if (!isChartVisibleAtTop(step)) {
          return false
        }
        const snapshot = resolveSnapshot(segment)
        if (!snapshot || !results) {
          return segment.status === 'current'
        }
        return predictionsMatch(snapshot.prediction, results.prediction)
      }
      return false
    },
    [step, survey?.key, resolveSnapshot, results, topMediaObscured],
  )

  const renderTooltipBody = useCallback(
    (segment: ProgressSegment) => {
      const title = segmentLabel(segment, t)
      const status = statusLabel(segment.status, t)

      if (segment.kind === 'questionnaire') {
        const thumb = segment.surveyKey ? portraitBySurveyKey[segment.surveyKey] : undefined
        const hideMedia = isMediaAlreadyShownAtTop(segment)
        const canOpen = !!thumb && segment.status !== 'upcoming' && !hideMedia
        const visible = segment.visibleQuestionCount
        const total = segment.totalQuestionCount
        // Primary figure = total questions; nuance when the conditional path is shorter.
        let questionsLine: string | null = null
        if (total != null && total > 0) {
          questionsLine =
            visible != null && visible > 0 && visible < total
              ? t('questionsCountWithPath', { count: total, path: visible })
              : t('questionsCount', { count: total })
        } else if (visible != null && visible > 0) {
          questionsLine = t('questionsCount', { count: visible })
        }
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            {questionsLine && <p className="EditionProgressBar-tooltip-body">{questionsLine}</p>}
            {hideMedia ? null : thumb ? (
              <div className="EditionProgressBar-tooltip-thumbWrap">
                <img className="EditionProgressBar-tooltip-thumb" src={thumb} alt="" />
              </div>
            ) : (
              segment.status !== 'upcoming' && (
                <p className="EditionProgressBar-tooltip-hint">{t('noIllustration')}</p>
              )
            )}
            {canOpen && <p className="EditionProgressBar-tooltip-action">{t('openIllustration')}</p>}
          </div>
        )
      }

      if (segment.pageId === 'consent') {
        const under18 = !isMajor
        const items: Array<{ checked: boolean; label: string }> = [
          { checked: !!hasReadConsent, label: t('consentCheck.privacy') },
          { checked: !!isMajor, label: t('consentCheck.age') },
          { checked: !!hasConditions, label: t('consentCheck.conditions') },
        ]
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            {under18 && (
              <p className="EditionProgressBar-tooltip-hint EditionProgressBar-tooltip-hint--green">
                {t('consentUnder18NoRecord')}
              </p>
            )}
            <ul className="EditionProgressBar-tooltip-checks">
              {items.map((item) => (
                <li
                  key={item.label}
                  className={
                    item.checked
                      ? 'EditionProgressBar-tooltip-checks-item--checked'
                      : 'EditionProgressBar-tooltip-checks-item--unchecked'
                  }
                >
                  <span aria-hidden>{item.checked ? '☑' : '☐'}</span>
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      }

      if (segment.pageId === 'email') {
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            {email ? (
              <p className="EditionProgressBar-tooltip-body">{email}</p>
            ) : (
              <p className="EditionProgressBar-tooltip-hint">{t('emailEmpty')}</p>
            )}
            <p className="EditionProgressBar-tooltip-hint EditionProgressBar-tooltip-hint--green">
              {t('emailNotLinked')}
            </p>
          </div>
        )
      }

      if (segment.pageId === 'acknowledge') {
        let detail = t('acknowledgeEmpty')
        if (hasAcknowledged === true) detail = t('acknowledgeSelf')
        if (hasAcknowledged === false) detail = t('acknowledgeTest')
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            <p className="EditionProgressBar-tooltip-body">{detail}</p>
          </div>
        )
      }

      if (segment.pageId === 'prediction') {
        // Do not leak a prediction result before the user has reached this step.
        const reached = segment.status !== 'upcoming'
        const name = !reached
          ? t('predictionEmpty')
          : prediction?.names?.short?.[lang] ||
            prediction?.names?.acr?.[lang] ||
            (predictionVeracity === true ? t('predictionYes') : undefined) ||
            (predictionVeracity === false ? t('predictionNo') : t('predictionEmpty'))
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            <p className="EditionProgressBar-tooltip-body">{name}</p>
          </div>
        )
      }

      if (segment.pageId === 'rectify') {
        const name =
          rectifiedPrediction?.names?.short?.[lang] ||
          rectifiedPrediction?.names?.acr?.[lang] ||
          t('rectifyEmpty')
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            <p className="EditionProgressBar-tooltip-body">{name}</p>
          </div>
        )
      }

      if (segment.pageId === 'probability') {
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            <p className="EditionProgressBar-tooltip-body">
              {voteProbability != null ? t('probabilityValue', { value: voteProbability }) : t('probabilityEmpty')}
            </p>
          </div>
        )
      }

      if (segment.pageId === 'forecasting') {
        const name =
          citizenForecasting?.names?.short?.[lang] ||
          citizenForecasting?.names?.acr?.[lang] ||
          t('forecastingEmpty')
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            <p className="EditionProgressBar-tooltip-body">{name}</p>
          </div>
        )
      }

      if (segment.pageId === 'results') {
        const snapshot = resolveSnapshot(segment)
        const hideMedia = isMediaAlreadyShownAtTop(segment)
        const canOpen = !!snapshot && segment.status !== 'upcoming' && !hideMedia
        let chart: React.ReactNode = hideMedia ? null : (
          <p className="EditionProgressBar-tooltip-hint">{t('resultsEmpty')}</p>
        )
        if (!hideMedia && snapshot) {
          chart = (
            <div className="EditionProgressBar-tooltip-chart">
              <ResultsChartThumb
                segment={segment}
                snapshot={snapshot}
                dsmpQ1={dsmpQ1}
                dsmpQ2={dsmpQ2}
              />
            </div>
          )
        }
        return (
          <div className="EditionProgressBar-tooltip">
            <p className="EditionProgressBar-tooltip-title">{title}</p>
            <p className="EditionProgressBar-tooltip-status">{status}</p>
            {chart}
            {canOpen && <p className="EditionProgressBar-tooltip-action">{t('openChart')}</p>}
          </div>
        )
      }

      return (
        <div className="EditionProgressBar-tooltip">
          <p className="EditionProgressBar-tooltip-title">{title}</p>
          <p className="EditionProgressBar-tooltip-status">{status}</p>
        </div>
      )
    },
    [
      t,
      lang,
      portraitBySurveyKey,
      email,
      hasReadConsent,
      hasConditions,
      isMajor,
      hasAcknowledged,
      prediction,
      predictionVeracity,
      rectifiedPrediction,
      voteProbability,
      citizenForecasting,
      resolveSnapshot,
      isMediaAlreadyShownAtTop,
      dsmpQ1,
      dsmpQ2,
    ],
  )

  const handleSegmentActivate = useCallback(
    (segment: ProgressSegment) => {
      if (isMediaAlreadyShownAtTop(segment)) {
        return
      }
      if (segment.kind === 'questionnaire') {
        const thumb = segment.surveyKey ? portraitBySurveyKey[segment.surveyKey] : undefined
        if (thumb && segment.status !== 'upcoming') {
          setModal({
            type: 'portrait',
            src: thumb,
            title: segmentLabel(segment, t),
          })
        }
        return
      }
      if (segment.pageId === 'results') {
        const snapshot = resolveSnapshot(segment)
        if (snapshot && segment.status !== 'upcoming') {
          setModal({
            type: 'chart',
            segment,
            title: segmentLabel(segment, t),
          })
        }
      }
    },
    [portraitBySurveyKey, resolveSnapshot, isMediaAlreadyShownAtTop, t],
  )

  const modalBody = useMemo(() => {
    if (!modal) return null
    if (modal.type === 'portrait') {
      return (
        <div className="EditionProgressBar-modalBody">
          <div className="EditionProgressBar-modalBody-media">
            <img src={modal.src} alt={modal.title} />
          </div>
        </div>
      )
    }
    const snapshot = resolveSnapshot(modal.segment)
    if (!snapshot) return null
    return (
      <div className="EditionProgressBar-modalBody">
        <div className="EditionProgressBar-modalBody-media">
          <ResultsChartView
            snapshot={snapshot}
            resultVariant={modal.segment.resultVariant}
            dsmpQ1={dsmpQ1}
            dsmpQ2={dsmpQ2}
          />
        </div>
      </div>
    )
  }, [modal, resolveSnapshot, dsmpQ1, dsmpQ2])

  if (!segments.length) {
    return null
  }

  // Constant Y under the bar (pinTippyYBelowBar). Only X follows the active segment.
  const tippyPopperOptions = {
    strategy: 'absolute' as const,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, TIPPY_BELOW_GAP],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: tippyBoundary(),
          padding: TIPPY_EDGE_PAD,
          mainAxis: false,
          altAxis: true,
          tether: false,
        },
      },
      clampTippyToAppColumnModifier(),
      pinTippyYBelowBarModifier(() => barRef.current),
      { name: 'flip', enabled: false },
      // adaptive relocates on Y when content is tall — disable so height stays pinned.
      {
        name: 'computeStyles',
        options: { adaptive: false },
      },
    ],
  }

  return (
    <>
      <Tippy
        singleton={tippySource}
        placement="bottom"
        interactive
        trigger="mouseenter focus click"
        touch={true}
        appendTo={tippyBoundary}
        delay={[80, 40]}
        duration={[120, 80]}
        maxWidth={tippyMaxWidth()}
        zIndex={200}
        popperOptions={tippyPopperOptions}
      />
      <div
        ref={barRef}
        className="EditionProgressBar"
        role="group"
        aria-label={t('ariaLabel')}
      >
        {segments.map((segment, index) => {
          const hideMedia = isMediaAlreadyShownAtTop(segment)
          const interactive =
            !hideMedia &&
            ((segment.kind === 'questionnaire' &&
              !!segment.surveyKey &&
              !!portraitBySurveyKey[segment.surveyKey] &&
              segment.status !== 'upcoming') ||
              (segment.pageId === 'results' &&
                !!resolveSnapshot(segment) &&
                segment.status !== 'upcoming'))
          const expanded = expandedSegmentId === segment.id
          const isFirst = index === 0
          const isLast = index === segments.length - 1

          return (
            <div
              key={`${segment.id}-${segment.status}-${segment.visibleQuestionCount ?? 'x'}-${segment.totalQuestionCount ?? 'x'}-${topMediaObscured ? 'o' : 'v'}-${segment.pageId === 'consent' && !isMajor ? 'u18' : 'ok'}`}
              className={[
                'EditionProgressBar-segSlot',
                isFirst ? 'EditionProgressBar-segSlot--first' : '',
                isLast ? 'EditionProgressBar-segSlot--last' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                flexGrow: segment.widthPct,
                zIndex: expanded ? segments.length + 2 : segments.length - index,
              }}
            >
              <Tippy
                content={renderTooltipBody(segment)}
                placement="bottom"
                singleton={tippyTarget}
                onShow={(instance: TippyInstance) => {
                  setExpandedSegmentId(segment.id)
                  const slot = instance.reference.parentElement as HTMLElement | null
                  const bar = barRef.current
                  if (slot?.classList.contains('EditionProgressBar-segSlot') && bar) {
                    instance.setProps({
                      getReferenceClientRect: () => slotReferenceRect(slot, bar),
                      maxWidth: tippyMaxWidth(),
                    })
                  }
                  instance.popperInstance?.update()
                }}
                onHide={() => {
                  setExpandedSegmentId((id) => (id === segment.id ? null : id))
                }}
              >
                <button
                  type="button"
                  className={[
                    'EditionProgressBar-segment',
                    `EditionProgressBar-segment--${segment.kind}`,
                    `EditionProgressBar-segment--${segment.status}`,
                    segmentTypeClass(segment),
                    isFirst ? 'EditionProgressBar-segment--first' : '',
                    isLast ? 'EditionProgressBar-segment--last' : '',
                    segment.pageId === 'consent' && !isMajor
                      ? 'EditionProgressBar-segment--consent-under18'
                      : '',
                    interactive ? 'EditionProgressBar-segment--interactive' : '',
                    expanded ? 'EditionProgressBar-segment--expanded' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-label={segmentLabel(segment, t)}
                  aria-current={segment.status === 'current' ? 'step' : undefined}
                  onClick={() => handleSegmentActivate(segment)}
                >
                  <span className="EditionProgressBar-segment-track">
                    <span
                      className={[
                        'EditionProgressBar-segment-fill',
                        segment.fillRatio >= 1 ? 'EditionProgressBar-segment-fill--full' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ width: `${Math.round(segment.fillRatio * 100)}%` }}
                    />
                  </span>
                  {segment.pageId === 'prediction' && (
                    <img
                      src={gotchiIcon}
                      alt=""
                      className="EditionProgressBar-segment-gotchi"
                      aria-hidden
                    />
                  )}
                </button>
              </Tippy>
            </div>
          )
        })}
      </div>
      <ModalDialog
        visible={!!modal}
        title={modal?.title}
        bodyComponent={modalBody}
        onClose={() => setModal(null)}
        extraClass="EditionProgressBar-modal"
      />
    </>
  )
})

export default EditionProgressBar
